import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { firestoreTruthyDisabled } from '../utils/resolveAssistantDoctorAssignment';
import { setViewerOnlySession } from '../utils/viewerOnly';

interface AuthContextType {
  username: string | null;
  login: (username: string) => void;
  logout: () => void;
  setUsername: (username: string | null) => void;
  isLoggedIn: () => boolean;
  authReady: boolean;
}

// Create a custom error for context
const createAuthContextError = () => {
  throw new Error('useAuth must be used within an AuthProvider');
};

// Default context with error-throwing methods
const defaultAuthContext: AuthContextType = {
  username: null,
  login: createAuthContextError,
  logout: createAuthContextError,
  setUsername: createAuthContextError,
  isLoggedIn: () => false,
  authReady: false
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [username, setUsername] = useState<string | null>(
    localStorage.getItem('username') || null
  );
  const [authReady, setAuthReady] = useState(false);

  const clearSession = useCallback(() => {
    setUsername(null);
    localStorage.removeItem('username');
    localStorage.removeItem('doctorId');
    localStorage.removeItem('assistantId');
    localStorage.removeItem('clinicId');
    localStorage.removeItem('token');
    setViewerOnlySession(false);
    window.dispatchEvent(new Event('doctorIdChanged'));
    window.dispatchEvent(new Event('assistantIdChanged'));
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        if (!user) clearSession();
        setAuthReady(true);
      },
      () => setAuthReady(true)
    );

    const handleAuthenticationFailure = () => {
      clearSession();
      void signOut(auth).catch(() => {});
    };
    window.addEventListener('apiAuthenticationFailed', handleAuthenticationFailure);
    return () => {
      unsubscribe();
      window.removeEventListener('apiAuthenticationFailed', handleAuthenticationFailure);
    };
  }, [clearSession]);

  useEffect(() => {
    let unsubscribeDoctor: (() => void) | null = null;
    let unsubscribeAssistant: (() => void) | null = null;

    const clearDisabledSession = () => {
      clearSession();
      void signOut(auth).catch(() => {});
    };

    const watchCurrentAccount = () => {
      unsubscribeDoctor?.();
      unsubscribeAssistant?.();
      unsubscribeDoctor = null;
      unsubscribeAssistant = null;

      const doctorId = (localStorage.getItem('doctorId') || '').trim();
      const assistantId = (localStorage.getItem('assistantId') || '').trim();

      if (doctorId) {
        unsubscribeDoctor = onSnapshot(doc(db, 'doctors', doctorId), (snapshot) => {
          if (snapshot.exists() && firestoreTruthyDisabled(snapshot.data()?.disabled)) {
            clearDisabledSession();
          }
          window.dispatchEvent(new Event('doctorPrivilegesChanged'));
        });
      }

      if (assistantId) {
        unsubscribeAssistant = onSnapshot(doc(db, 'assistants', assistantId), (snapshot) => {
          if (snapshot.exists() && firestoreTruthyDisabled(snapshot.data()?.disabled)) {
            clearDisabledSession();
          }
        });
      }
    };

    watchCurrentAccount();
    window.addEventListener('doctorIdChanged', watchCurrentAccount);
    window.addEventListener('assistantIdChanged', watchCurrentAccount);
    return () => {
      window.removeEventListener('doctorIdChanged', watchCurrentAccount);
      window.removeEventListener('assistantIdChanged', watchCurrentAccount);
      unsubscribeDoctor?.();
      unsubscribeAssistant?.();
    };
  }, [clearSession]);

  const login = (user: string) => {
    setUsername(user);
    // Store username in localStorage for persistence
    localStorage.setItem('username', user);
  };

  const logout = () => {
    clearSession();
  };
  
  const isLoggedIn = (): boolean => {
    // Check for stored username or doctorId
    return authReady && (
      !!username || !!localStorage.getItem('username') || !!localStorage.getItem('doctorId')
    );
  };

  return (
    <AuthContext.Provider value={{ 
      username, 
      login, 
      logout,
      setUsername,
      isLoggedIn,
      authReady
    }}>
      {children}
    </AuthContext.Provider> 
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  // Ensure the hook is used within AuthProvider
  if (context === defaultAuthContext) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
